import { useEffect } from 'react';

import { Link2, LogIn, Shield, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';

import { useOffice } from '@/components/providers/OfficeProvider';
import { Button } from '@/components/shadcn/button';
import useAuth from '@/hooks/useAuth';
import paths from '@/lib/paths';

export default function Login() {
  const { t } = useTranslation();
  const { login, isAuthenticated, isPollingLogin } = useAuth();
  const navigate = useNavigate();
  const { isOfficeEnvironment } = useOffice();

  useEffect(() => {
    if (!isAuthenticated) return;

    navigate(generatePath(paths.assistant));
  }, [isAuthenticated, navigate]);

  return (
    <div className="bg-background text-foreground font-body flex h-full min-h-screen w-full items-center justify-center">
      <div className="m-4 flex w-full flex-col items-center gap-4 rounded border p-4 py-8">
        <Shield className="text-primary-500 h-8 w-8" />

        <div className="flex flex-col items-center gap-2">
          <h1 className="text-center text-2xl font-medium">
            {t('AUTH_LOGIN_TITLE')}
          </h1>
          <p className="text-muted-foreground">{t('AUTH_LOGIN_SUBTITLE')}</p>
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-muted-foreground flex items-start gap-2 text-sm">
            <Sparkles className="text-primary mt-0.5 h-4 w-4" />
            <span>{t('AUTH_LOGIN_VALUE_LINE_1')}</span>
          </div>

          <div className="text-muted-foreground flex items-start gap-2 text-sm">
            <Link2 className="text-primary mt-0.5 h-4 w-4" />
            <span>{t('AUTH_LOGIN_VALUE_LINE_2')}</span>
          </div>
        </div>

        <Button onClick={login} disabled={isPollingLogin}>
          <LogIn />
          {t('AUTH_LOGIN_BUTTON')}
        </Button>

        <p className="text-muted-foreground text-center text-xs">
          {isOfficeEnvironment
            ? t('AUTH_INFO_OFFICE')
            : t('AUTH_INFO_REDIRECT')}
        </p>

        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-muted-foreground text-xs">
            {t('AUTH_SIGNUP_PROMO')}{' '}
            <a
              href="https://onboarding.wegroup.be/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary underline"
            >
              {t('AUTH_SIGNUP_LINK')}
            </a>
          </p>

          <p className="text-muted-foreground text-xs">
            {t('AUTH_SUPPORT_PROMO')}{' '}
            <a
              href={t('AUTH_SUPPORT_URL')}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary underline"
            >
              {t('AUTH_SUPPORT_LINK')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
